import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { SpeakerCard } from "@/components/site/SpeakerCard";
import { useSite } from "@/components/site/site-data";
import { PageHeader } from "@/components/site/PageHeader";

export const Route = createFileRoute("/speakeri")({
  head: () => ({
    meta: [
      { title: "Speakeri & Moderatori — InvesTenergy 2026" },
      {
        name: "description",
        content:
          "Speakerii și moderatorii Conferinței anuale InvesTenergy 2026 și ai Galei Specialiștilor din Energie.",
      },
      { property: "og:title", content: "Speakeri & Moderatori — InvesTenergy 2026" },
      {
        property: "og:description",
        content: "Responsabilitate în energie. Puterea de a schimba.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Speakeri,
});

type TabKey = "speaker" | "moderator";

const tabs: { key: TabKey; label: string }[] = [
  { key: "speaker", label: "Speakeri" },
  { key: "moderator", label: "Moderatori" },
];

function Speakeri() {
  const { blocks, speakers } = useSite();
  const [active, setActive] = useState<TabKey>("speaker");

  const filtered = speakers.filter((s) =>
    active === "speaker" ? s.category === "speaker" : s.category !== "speaker"
  );

  return (
    <main>
      <PageHeader title="Speakeri & Moderatori" subtitle={blocks["speakeri_slogan"]?.body ?? ""} />
      <section className="mx-auto max-w-6xl px-6 pt-16">
        <p className="text-lg leading-relaxed whitespace-pre-line text-foreground/90">
          {blocks["speakeri_intro"]?.body}
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-12">
        <div className="flex justify-center">
          <div className="relative inline-flex items-center gap-1 rounded-full border border-border bg-card p-1.5 shadow-sm">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className="relative z-10 px-6 py-2.5 text-sm font-semibold tracking-wide transition-colors sm:px-10 sm:text-base"
                aria-pressed={active === tab.key}
              >
                {active === tab.key && (
                  <motion.span
                    layoutId="speaker-tab"
                    className="absolute inset-0 rounded-full bg-primary"
                    transition={{ type: "spring", stiffness: 320, damping: 28 }}
                  />
                )}
                <span
                  className={`relative z-10 ${
                    active === tab.key ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="py-14"
          >
            {active === "moderator" && (
              <div className="space-y-16">
                {[
                  { key: "moderator_conf", label: "Moderatori Conferința InvesTenergy" },
                  { key: "moderator_gala", label: "Moderatori Gala Specialiștilor" },
                ].map((sub) => {
                  const list = filtered.filter((s) => s.category === sub.key);
                  if (list.length === 0) return null;
                  return (
                    <div key={sub.key}>
                      <h2 className="mb-8 text-center font-display text-2xl font-bold tracking-tight md:text-3xl">
                        {sub.label}
                      </h2>
                      <div className="mx-auto grid max-w-4xl gap-8 sm:grid-cols-2 md:grid-cols-3">
                        {list.map((s, i) => (
                          <SpeakerCard key={s.id} speaker={s} index={i} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}


            {active === "speaker" && (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                {filtered.map((s, i) => (
                  <SpeakerCard key={s.id} speaker={s} index={i} />
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </section>
    </main>
  );
}
